import { SchemaObject, Annotation, DeleteOperation, SignalOperation, AddOperation, AnnotationState, UserContextHolder, Document, ContextHolder, UpdateOperation, TenantContextHolder, SubDocument, DeltaUpdateOperation, Operation, Signal } from '@augloop/types-core';
import { WorkflowDefinitionScope, WorkflowKind } from '@augloop/interfaces';
import { AnnotationMetaDataChange, Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { AnnotationResultsMessage, ServerAuthenticationState } from '@augloop/session-protocol';
import { WorkflowDefinitionOverrideMessage } from '@augloop/session-protocol-internal';
import { AnnotationSource, LocalWorkflowOrchestrationMode } from './session-proxy';
import { WorkflowModel } from './workflow-model';
import { getSafeNumber } from './utils';
import { applyFormattedTextTileDelta, applyTextTileDelta, ExecutionState, ContextIdManager, Location } from '@augloop/mastermind-shared';
import { LocalWorkflowContext } from './local-workflow-context';
import { getContextTypes } from '@augloop/workflow-requested-contexts';
import { TextTileDelta, FormattedTextTileDelta } from '@augloop/types-text';
import { shallowCloneItemWithPath } from '@augloop/session-cache';
import { WorkflowExecutionTracker } from './local-workflow-execution-tracker';
const delayMillisecondsIfRequiredContextsNotReady = 500;
var TriggerReason;
(function (TriggerReason) {
    TriggerReason["Unknown"] = "";
    TriggerReason["InputReceived"] = "input";
    TriggerReason["JoinMaxAnnnotation"] = "maxAnnotation";
    TriggerReason["JoinMaxTimeout"] = "maxTimeout";
    TriggerReason["JoinEarlyCompletion"] = "earlyCompletion";
})(TriggerReason || (TriggerReason = {}));
export class LocalWorkflowExecutionManager {
    constructor(modelDownloader, inferenceServiceFactory, options) {
        this.executionTrackersByWorkflowNameBySession = new Map();
        this.workflowsWithSessionAffinity = new Map();
        this.workflowDefinitionsWithSessionAffinity = [];
        this.workflowsWithoutSessionAffinity = [];
        this.pendingScopeExecutionNotificationsByWorkflow = new Map();
        this.sweepIntervalMs = 200;
        this.sweepTimers = new Map();
        this.getResourceAsArrayBuffer = (modelId, version, modelFilters) => {
            if (this.modelDownloader) {
                return this.modelDownloader.getResourceAsArrayBuffer(modelId, version, modelFilters);
            }
            else {
                return Promise.reject(new Error('Resource Downloader never created'));
            }
        };
        this.getResourceAsURL = (modelId, version, modelFilters) => {
            if (this.modelDownloader) {
                return this.modelDownloader.getResourceAsURL(modelId, version, modelFilters);
            }
            else {
                return Promise.reject(new Error('Resource Downloader never created'));
            }
        };
        this.createModel = (modelUrl) => {
            if (!this.inferenceService && this.inferenceServiceFactory) {
                this.inferenceService = this.inferenceServiceFactory();
            }
            if (this.inferenceService) {
                return this.inferenceService.then((service) => {
                    return service.createModel(modelUrl);
                });
            }
            else {
                return Promise.reject(new Error('Inference Service never created'));
            }
        };
        this.createModelInputs = () => {
            if (!this.inferenceService && this.inferenceServiceFactory) {
                this.inferenceService = this.inferenceServiceFactory();
            }
            if (this.inferenceService) {
                return this.inferenceService.then((service) => {
                    return service.createInputs();
                });
            }
            else {
                throw new Error('Inference Service never created');
            }
        };
        this.nextAnnotationId = 1;
        this.nextSignalId = 1;
        this.modelDownloader = modelDownloader;
        this.inferenceServiceFactory = inferenceServiceFactory;
        // Set TextTile data handler
        if (options.enableDeltas) {
            this.deltaHandlers = new Map()
                .set(SchemaObject.getTypeNameFor(TextTileDelta), applyTextTileDelta)
                .set(SchemaObject.getTypeNameFor(FormattedTextTileDelta), applyFormattedTextTileDelta);
            this.itemsForDelta = new Map();
        }
        this.enableEarlyJoin = options.enableEarlyJoin || false;
        this.site = {
            getResourceAsArrayBuffer: this.getResourceAsArrayBuffer,
            getResourceAsURL: this.getResourceAsURL,
            createModel: this.createModel,
            createModelInputs: this.createModelInputs
        };
    }
    getNextClientAnnotationId() {
        return '#AC' + this.nextAnnotationId++;
    }
    getNextClientSignalId() {
        return '#SC' + this.nextSignalId++;
    }
    registerLocalWorkflow(workflow, session) {
        const op = new LogOperation({
            operationName: 'WorkflowRegistration',
            resourceId: workflow.id
        }).start();
        if (workflow.inputTypes.length === 0) {
            throw new Error(`Invalid workflow params`);
        }
        if (session) {
            // call from session.registerWorkflow
            this.workflowsWithSessionAffinity.get(session).push(this.createWorkflowImplementation(workflow, session));
            session.registerContextTypes(getContextTypes(workflow.requestedContextTypesRules).map(([type, _cardinality]) => type));
            session.attachToWorkflowGraph(workflow);
            op.setClientMetadata(session.getClientMetadata());
        }
        else {
            // call from runtime.registerWorkflow
            if (workflow.isStateful) {
                this.workflowDefinitionsWithSessionAffinity.push(workflow);
                this.workflowsWithSessionAffinity.forEach((workflows, session) => {
                    workflows.push(this.createWorkflowImplementation(workflow, session));
                });
            }
            else {
                this.workflowsWithoutSessionAffinity.push(this.createWorkflowImplementation(workflow));
            }
            this.workflowsWithSessionAffinity.forEach((_workflows, session) => {
                session.registerContextTypes(getContextTypes(workflow.requestedContextTypesRules).map(([type, _cardinality]) => type));
                // Add workflow into workflowGraph of every active session
                session.attachToWorkflowGraph(workflow);
            });
        }
        op.success = true;
        Log.info(0x2224d0de /* tag_8jnd4 */, LogCategory.CoreDefault, op.stop());
    }
    getAllRegisteredWorkflowsFromSession(session) {
        const workflowsImplementation = [];
        workflowsImplementation.push(...(this.workflowsWithoutSessionAffinity || []));
        workflowsImplementation.push(...(this.workflowsWithSessionAffinity.get(session) || []));
        return workflowsImplementation.map(workflowImplementation => workflowImplementation.workflow);
    }
    getWorkflowDefinitionsByName(session) {
        var _a;
        const map = new Map();
        (_a = this.workflowsWithSessionAffinity.get(session)) === null || _a === void 0 ? void 0 : _a.forEach(w => map.set(w.workflow.id, w.workflow));
        return map;
    }
    getWorkflowDefinitionsWithSessionAffinity() {
        return this.workflowDefinitionsWithSessionAffinity;
    }
    attachExecutionTrackerToEachWorkflow(workflowGraph, session, onWorkflowExecutionCompleteCallback) {
        const workflowNodes = workflowGraph.getWorkflowNodes();
        const executionTrackersByWorkflowName = this.executionTrackersByWorkflowNameBySession.get(session);
        const onContextIdWorkflowExecutionComplete = (workflowId, contextId) => {
            if (onWorkflowExecutionCompleteCallback) {
                onWorkflowExecutionCompleteCallback(workflowId, contextId);
            }
            if (this.enableEarlyJoin) {
                // Try execute pending scope execution notifications if there is a pending join workflow to be executed
                const hasJoinPendingToExecute = () => {
                    for (const executionTracker of this.executionTrackersByWorkflowNameBySession.get(session).values()) {
                        if (executionTracker.graphNode.workflow.kind === WorkflowKind.Join) {
                            for (const scopeContextId of executionTracker.getExecutionState().keys()) {
                                if (ContextIdManager.isParentContextId(scopeContextId, contextId)) {
                                    return true;
                                }
                            }
                        }
                    }
                    return false;
                };
                if (hasJoinPendingToExecute()) {
                    this.cancelSweepTimer(session);
                    this.ensureSweepTimer(session);
                    this.sweepScopeExecutionNotifications();
                }
            }
        };
        for (const node of workflowNodes) {
            const oldTracker = executionTrackersByWorkflowName.get(node.workflow.id);
            const workflowExecutionTracker = new WorkflowExecutionTracker(node, onContextIdWorkflowExecutionComplete.bind(this), oldTracker);
            executionTrackersByWorkflowName.set(node.workflow.id, workflowExecutionTracker);
        }
        for (const workflowExecutionTracker of executionTrackersByWorkflowName.values()) {
            this.setDownstreamWorkflowExecutionTrackers(workflowExecutionTracker, executionTrackersByWorkflowName);
            this.setUpstreamWorkflowExecutionTrackers(workflowExecutionTracker, executionTrackersByWorkflowName);
        }
    }
    canActivateWorkflow(graphNode, session) {
        var _a;
        // 1. Client workflow are always activated by default
        if (graphNode.location === Location.Local) {
            return true;
        }
        // 2. Server workflows are activated if session is connected (anonymous token set)
        if (!session.hasConnected) {
            return false;
        }
        // 3. Server workflows that has requiredTokenTypes are activated if serverAuthState is Authenticated or Pending
        if (((_a = graphNode.workflow.requiredTokenTypes) === null || _a === void 0 ? void 0 : _a.length) > 0 && session.getServerAuthenticationState() === ServerAuthenticationState.NotAuthenticated) {
            return false;
        }
        return true;
    }
    setDownstreamWorkflowExecutionTrackers(workflowExecutionTracker, executionTrackersByWorkflowName) {
        if (workflowExecutionTracker.downstreamWorkflowExecutionTrackers !== undefined) {
            return;
        }
        workflowExecutionTracker.downstreamWorkflowExecutionTrackers = new Set();
        const { graphNode } = workflowExecutionTracker;
        for (const workflowNode of graphNode.downstreamWorkflows || []) {
            const downstreamExecutionTracker = executionTrackersByWorkflowName.get(workflowNode.workflow.id);
            this.setDownstreamWorkflowExecutionTrackers(downstreamExecutionTracker, executionTrackersByWorkflowName);
            workflowExecutionTracker.downstreamWorkflowExecutionTrackers.add(downstreamExecutionTracker);
        }
    }
    setUpstreamWorkflowExecutionTrackers(workflowExecutionTracker, executionTrackersByWorkflowName) {
        if (workflowExecutionTracker.upstreamWorkflowExecutionTrackers !== undefined) {
            return;
        }
        workflowExecutionTracker.upstreamWorkflowExecutionTrackers = new Set();
        const { graphNode } = workflowExecutionTracker;
        for (const workflowNode of graphNode.upstreamWorkflows || []) {
            const upstreamExecutionTracker = executionTrackersByWorkflowName.get(workflowNode.workflow.id);
            this.setUpstreamWorkflowExecutionTrackers(upstreamExecutionTracker, executionTrackersByWorkflowName);
            workflowExecutionTracker.upstreamWorkflowExecutionTrackers.add(upstreamExecutionTracker);
        }
    }
    deactivateServerWorkflow(graphNode, session) {
        var _a;
        // Local registered workflows are always activated by default (can not be deactivated)
        if (graphNode.location === Location.Local) {
            return;
        }
        // We need to clear server workflow states on the tracker, since they won't execute at all.
        (_a = this.executionTrackersByWorkflowNameBySession.get(session)) === null || _a === void 0 ? void 0 : _a.get(graphNode.workflow.id).clearWorkflowExecutions();
        graphNode.isActivated = false;
    }
    addSession(session) {
        this.executionTrackersByWorkflowNameBySession.set(session, new Map());
        const sessionWorkflowImplementation = [];
        for (const workflow of this.workflowDefinitionsWithSessionAffinity) {
            sessionWorkflowImplementation.push(this.createWorkflowImplementation(workflow));
            session.attachToWorkflowGraph(workflow);
        }
        this.workflowsWithSessionAffinity.set(session, sessionWorkflowImplementation);
        // We need to add those stateless workflows registered on the runtime in the WorkflowGraph too
        for (const workflowImplementation of this.workflowsWithoutSessionAffinity) {
            session.attachToWorkflowGraph(workflowImplementation.workflow);
        }
    }
    closeSession(session) {
        this.cancelSweepTimer(session);
        for (const workflowImplementation of this.workflowsWithSessionAffinity.get(session) || []) {
            workflowImplementation.workflowLambda.dispose();
        }
        this.workflowsWithSessionAffinity.delete(session);
    }
    setTokenCallback(getTokenCallback) {
        this.getAuthTokenCallback = getTokenCallback;
    }
    preProcessItemToWorkflow(workflow, item, session) {
        const executionTracker = this.executionTrackersByWorkflowNameBySession.get(session).get(workflow.id);
        // We are only tracking inputs to Join Workflows
        if (workflow.kind === WorkflowKind.Join && SchemaObject.matchesTypesFor(item.body, workflow.inputTypes)) {
            executionTracker.addInputItemToProcess(item.contextId);
        }
        // Update workflow execution state
        if (workflow.kind === WorkflowKind.SingleItem && SchemaObject.matchesTypesFor(item.body, workflow.inputTypes)
            || workflow.kind === WorkflowKind.Join && SchemaObject.matchesTypesFor(item.body, [workflow.collectionScopeType])) {
            executionTracker.beforeWorkflowExecution(item.contextId);
        }
    }
    isReadyToEarlyJoin(executionTracker, scopeContextId, clientMetadata) {
        const logTelemetry = (info) => {
            var _a;
            const statesStr = Array.from((_a = info.states) !== null && _a !== void 0 ? _a : [])
                .map((v) => `${v[0]}: ${v[1].map(states => states.join())}`)
                .join();
            const logOp = new LogOperation({
                operationName: 'EarlyJoinCompletion',
                resourceId: executionTracker.graphNode.workflow.id,
                joinContextId: scopeContextId,
                success: true
            }).start();
            logOp.setClientMetadata(clientMetadata);
            logOp.resultDescription = `isReadyToEarlyJoin (${this.enableEarlyJoin}) -> hasProcessedAllInputItems: ${info.hasProcessedAllInputItems}, allUpstreamComplete: ${info.allUpstreamComplete}, states: ${statesStr}`;
            Log.info(0x1e8ce790 /* tag_49o4q */, LogCategory.CoreDefault, logOp.stop());
        };
        /**
         * The allUpstreamComplete is the most important check to make for the early completion, but hasProcessedAllInputItems is also needed.
         * Consider the following case:
         *
         *    WA
         *   /  \
         *  WB  WC
         *   \  /
         *    JW
         *
         * When WB and WC are running. WB finishes before WC. WB produces annotation X that goes to the Join JW.
         * When JW process X, hasProcessedAllInputItem will be true but it still needs to wait for WC to finish its execution.
         */
        const hasProcessedAllInputItems = executionTracker.countItemsToProcess(scopeContextId) === 0;
        if (!hasProcessedAllInputItems) {
            if (!this.enableEarlyJoin) {
                logTelemetry({ hasProcessedAllInputItems });
            }
            return false;
        }
        /**
         * Now, consider that WC produces 2 annotations Y1 and Y2 that also goes to the Join JW.
         * When JW process Y1, allUpstreamComplete will already be true, but there is still Y2 for JW to process. That is why we also need the hasProcessedAllInputItem check here
         */
        const { allUpstreamComplete, states } = this.areAllUpstreamWorkflowComplete(executionTracker, scopeContextId);
        if (!this.enableEarlyJoin) {
            logTelemetry({ hasProcessedAllInputItems, allUpstreamComplete, states });
            return false;
        }
        return allUpstreamComplete;
    }
    areAllUpstreamWorkflowComplete(executionTracker, scopeContextId) {
        const upstreamStates = new Map();
        for (const upstreamExecutionTracker of executionTracker.upstreamWorkflowExecutionTrackers) {
            const states = [];
            for (const [contextId, state] of upstreamExecutionTracker.getExecutionState()) {
                states.push([contextId, state]);
                if (ContextIdManager.isParentContextId(scopeContextId, contextId)) {
                    /**
                     * If there is one valid contextId under the scope of the scopeContextId this means
                     * that the execution state is in a state that is not Complete (Pending, Running, ...)
                     */
                    upstreamStates.set(upstreamExecutionTracker.graphNode.workflow.id, states);
                    return { allUpstreamComplete: false, states: upstreamStates };
                }
            }
            if (states.length > 0) {
                upstreamStates.set(upstreamExecutionTracker.graphNode.workflow.id, states);
            }
        }
        return { allUpstreamComplete: true, states: upstreamStates };
    }
    runLocalWorkflows(operations, session, addContextHolders = false) {
        var _a, _b, _c;
        const checkItemAndExecute = (workflowImplementation, opItem, operation) => {
            var _a, _b;
            const workflowItemStorage = session.getWorkflowItemStorage();
            const { workflow } = workflowImplementation;
            const executionTracker = (_a = this.executionTrackersByWorkflowNameBySession.get(session)) === null || _a === void 0 ? void 0 : _a.get(workflow.id);
            const inputTypes = workflow.inputTypes.concat(workflow.kind === WorkflowKind.Join ? workflow.collectionScopeType : []);
            const logOp = new LogOperation({
                operationName: 'RunLocalWorkflows',
                success: true,
                resourceId: workflow.id,
                joinContextId: opItem.contextId
            }).start();
            logOp.setClientMetadata(session.getClientMetadata());
            if (SchemaObject.matchesTypesFor(opItem.body, inputTypes)) {
                const itemWithPath = shallowCloneItemWithPath(operation.parentPath, opItem);
                if (workflow.kind === WorkflowKind.Join) {
                    if (SchemaObject.matchesTypesFor(itemWithPath.body, [workflow.collectionScopeType])) {
                        workflowItemStorage.setScopeItem(itemWithPath, workflow);
                        logOp.resultDescription = `Scope item: ${opItem.id} (${SchemaObject.getTypeNameFor(opItem.body)})`;
                        Log.info(0x1f3d87c9 /* tag_5py5j */, LogCategory.CoreDefault, logOp.stop());
                        this.setScopeExecutionNotification(session, workflowImplementation, itemWithPath, LocalWorkflowOrchestrationMode.Internal);
                        return;
                    }
                    else {
                        executionTracker.removeProcessedInputItem(itemWithPath.contextId);
                        const collectionScopeItem = workflowItemStorage.getScopeItem(itemWithPath.contextId, workflow);
                        if (!collectionScopeItem) {
                            logOp.resultDescription = `Filtered out join invalidation: out of scope type (${workflow.collectionScopeType}), item id: ${itemWithPath.id}`;
                            Log.info(0x2041a886 /* tag_6q08g */, LogCategory.CoreDefault, logOp.stop());
                            return;
                        }
                        // Add this new item to the annotations list of this ContextId
                        workflowItemStorage.addItemToWorkflowList(itemWithPath, workflow);
                        logOp.joinContextId = collectionScopeItem.contextId;
                        logOp.resultDescription = `Input item: ${opItem.id} (${SchemaObject.getTypeNameFor(opItem.body)})`;
                    }
                }
                // Since we don't have session cache locally yet, we have a workaround for user context producer workflows by adding UserContextHolder to each submitted operations array.
                // However, we need to avoid trigger user (and tenant, group) context producer workflow on each submit and to run it just once.
                // TODO #5326116: Adapt this once session cache is available for local workflows.
                if (SchemaObject.getBaseTypesFor(itemWithPath.body).indexOf(ContextHolder.getTypeName()) >= 0) {
                    const contextHolderPath = [...itemWithPath.parentPath, itemWithPath.id];
                    let allContextsAlreadyAvailable = true;
                    for (const contextOutputType of (_b = workflow.outputTypes) !== null && _b !== void 0 ? _b : []) {
                        if (!session.getContextAnnotations(contextOutputType, AnnotationSource.LocalWorkflow, contextHolderPath, workflow.id)) {
                            allContextsAlreadyAvailable = false;
                            break;
                        }
                    }
                    // If all contexts are already available on a given path, then skip execution of this context producer workflow.
                    if (allContextsAlreadyAvailable) {
                        return;
                    }
                }
                const execute = () => {
                    var _a;
                    const [areRequiredContextsReady, requestedContexts] = session.resolveRequestedContexts(workflow);
                    if (!areRequiredContextsReady) {
                        logOp.resultDescription = `Required contexts are not ready for ${workflow.id}. Retrying execution in ${delayMillisecondsIfRequiredContextsNotReady} milliseconds...`;
                        Log.info(0x2088d0cb /* tag_68ndl */, LogCategory.CoreDefault, logOp.stop());
                        setTimeout(execute, delayMillisecondsIfRequiredContextsNotReady);
                        return;
                    }
                    if (workflow.kind === WorkflowKind.SingleItem) {
                        this.queueWorkflow({
                            workflowInfo: workflowImplementation,
                            scopeItem: itemWithPath,
                            inputItems: [itemWithPath],
                            requestedContexts,
                            session,
                            orchestrationMode: LocalWorkflowOrchestrationMode.Internal,
                            triggerReason: TriggerReason.InputReceived,
                            onCompleteCallback: this.onWorkflowExecuted.bind(this, itemWithPath, workflow, session)
                        })
                            .then(() => {
                            Log.info(0x1e5913d7 /* tag_4wrpx */, LogCategory.WorkflowDefault, logOp.stop());
                        })
                            .catch((error) => {
                            logOp.resultDescription = error;
                            Log.error(0x2224d0df /* tag_8jnd5 */, LogCategory.CoreDefault, logOp.stop());
                        });
                    }
                    else if (workflow.kind === WorkflowKind.Join) {
                        const contextId = opItem.contextId;
                        const scopeItem = workflowItemStorage.getScopeItem(contextId, workflowImplementation.workflow);
                        if (!scopeItem) {
                            logOp.resultDescription = `No scope item for ${workflow.id} workflow from contextId ${contextId}`;
                            Log.info(0x1f65b24b /* tag_5z1jl */, LogCategory.CoreDefault, logOp.stop());
                            return;
                        }
                        const isReadyToEarlyJoin = this.isReadyToEarlyJoin(executionTracker, scopeItem.contextId, session.getClientMetadata());
                        const triggerReason = isReadyToEarlyJoin ? TriggerReason.JoinEarlyCompletion : TriggerReason.JoinMaxAnnnotation;
                        if (workflowItemStorage.isWorkflowReady(scopeItem.contextId, workflow) || isReadyToEarlyJoin) {
                            const scopeExecutionNotification = (_a = this.pendingScopeExecutionNotificationsByWorkflow.get(workflow.id)) === null || _a === void 0 ? void 0 : _a.get(scopeItem.contextId);
                            if (!scopeExecutionNotification) {
                                logOp.resultDescription = `Workflow ${workflow.id}, contextId ${scopeItem.contextId}, already queued, skipping new scope execution`;
                                Log.info(0x1f796351 /* tag_54wnr */, LogCategory.CoreDefault, logOp.stop());
                                return;
                            }
                            const inputItems = workflowItemStorage.getItemsToExecute(scopeItem.contextId, workflow);
                            this.queueWorkflow({
                                workflowInfo: workflowImplementation,
                                scopeItem,
                                inputItems,
                                requestedContexts,
                                session,
                                orchestrationMode: LocalWorkflowOrchestrationMode.Internal,
                                triggerReason,
                                onCompleteCallback: this.onWorkflowExecuted.bind(this, scopeItem, workflow, session)
                            })
                                .then(() => {
                                Log.info(0x1e5913d6 /* tag_4wrpw */, LogCategory.WorkflowDefault, logOp.stop());
                            })
                                .catch((error) => {
                                logOp.resultDescription = error;
                                Log.error(0x2041a887 /* tag_6q08h */, LogCategory.CoreDefault, logOp.stop());
                            });
                            // Remove notification from the pending scope execution map
                            this.pendingScopeExecutionNotificationsByWorkflow.get(workflow.id).delete(scopeItem.contextId);
                            if (this.pendingScopeExecutionNotificationsByWorkflow.get(workflow.id).size === 0) {
                                this.pendingScopeExecutionNotificationsByWorkflow.delete(workflow.id);
                            }
                        }
                    }
                };
                execute();
            }
        };
        let currentOperations = operations;
        if (addContextHolders) {
            // Prepend artificial UserContextHolder and TenantContextHolder to provided operations, so local user and tenant context producer workflows can have input and get executed.
            // Prepend instead of append as a best effort try to have context created before execution of context consumer workflows.
            //
            // TODO #5326116: Refactor this to use UserContextHolder and TenantContextHolder from session cache once it's available for local workflows.
            currentOperations = [
                new AddOperation({ parentPath: ['session'], items: [{ id: '#userContext#', body: new UserContextHolder() }] }),
                new AddOperation({ parentPath: ['session'], items: [{ id: '#tenantContext#', body: new TenantContextHolder() }] }),
            ];
            session.getContextIdManager().applyContextIdOnOperations(currentOperations);
            currentOperations = currentOperations.concat(operations);
        }
        for (const operation of currentOperations) {
            const opType = SchemaObject.getTypeNameFor(operation);
            for (const opItem of operation.items) {
                session.applyOperationForContext(operation, opItem, AnnotationSource.Submitted);
                // Currently, limited AL cache is stored/available locally
                // Check that the item(s) in question is not being deleted
                if (opType === DeleteOperation.getTypeName()) {
                    (_a = this.itemsForDelta) === null || _a === void 0 ? void 0 : _a.delete(operation.parentPath.concat(opItem.id).toString());
                    continue;
                }
                else if (!opItem.body) {
                    continue;
                }
                else if (opType === DeltaUpdateOperation.getTypeName() && this.deltaHandlers && this.itemsForDelta) {
                    this.handleLocalDeltaUpdate(opItem, operation, session);
                    continue;
                }
                else {
                    (_b = this.itemsForDelta) === null || _b === void 0 ? void 0 : _b.set(operation.parentPath.concat(opItem.id).toString(), opItem);
                }
                for (const workflowImplementation of this.workflowsWithoutSessionAffinity) {
                    checkItemAndExecute(workflowImplementation, opItem, operation);
                }
                for (const workflowImplementation of (_c = this.workflowsWithSessionAffinity.get(session)) !== null && _c !== void 0 ? _c : []) {
                    checkItemAndExecute(workflowImplementation, opItem, operation);
                }
            }
        }
    }
    handleLocalDeltaUpdate(opItem, deltaOperation, session) {
        const deltaLogOp = new LogOperation({
            operationName: 'LocalDeltaUpdate',
            dimension0: SchemaObject.getTypeNameFor(opItem.body),
            success: true
        });
        deltaLogOp.start();
        try {
            // Ensure the appropriate handler and base item are available
            const handler = this.deltaHandlers.get(SchemaObject.getTypeNameFor(opItem.body));
            // Delta provided should be applied to last item in its parent path
            const tileItem = this.itemsForDelta.get(deltaOperation.parentPath.toString());
            if (handler && tileItem) {
                // Delta operations parentPath contains the tile id on it. Since we are reconstructing the tile, the tile parentPath does not contains its own id.
                // Ex.:
                // TextTile:
                // - id: xyz
                // - parentPath: ['session', 'doc']
                // TextTileDelta:
                // - id: abc
                // - parentPath: ['session', 'doc', 'xyz']
                const updatedItemParentPath = deltaOperation.parentPath.length > 0 ? deltaOperation.parentPath.slice(0, deltaOperation.parentPath.length - 1) : deltaOperation.parentPath;
                // Update the base item using the handler and provided tileItem
                const updatedItemBody = handler(opItem.body, tileItem.body);
                if (updatedItemBody) {
                    const updatedItem = {
                        id: tileItem.id,
                        revId: opItem.revId,
                        body: updatedItemBody,
                        parentPath: updatedItemParentPath,
                        delta: opItem.body,
                        contextId: opItem.contextId
                    };
                    // Create new operation to trigger workflows that will take the new item as input
                    const updatedOperation = new Operation({ parentPath: updatedItem.parentPath, items: [updatedItem] });
                    Log.info(0x202a3757 /* tag_6k93x */, LogCategory.CoreDefault, deltaLogOp.stop());
                    // Only run local workflows as this is handled separately on the server
                    this.runLocalWorkflows([updatedOperation], session);
                }
                else {
                    deltaLogOp.success = false;
                    deltaLogOp.resultDescription = `Failed because the handler did not produce valid updated item`;
                    Log.info(0x202a3758 /* tag_6k93y */, LogCategory.CoreDefault, deltaLogOp.stop());
                }
            }
            else {
                deltaLogOp.success = false;
                deltaLogOp.resultDescription = `Failed due to lack of handler or parent item`;
                Log.info(0x202a3759 /* tag_6k93z */, LogCategory.CoreDefault, deltaLogOp.stop());
            }
        }
        catch (error) {
            deltaLogOp.success = false;
            deltaLogOp.resultDescription = `Failed to apply delta, error: ${error}`;
            Log.info(0x202a375a /* tag_6k930 */, LogCategory.CoreDefault, deltaLogOp.stop());
        }
    }
    createWorkflowImplementation(workflow, session) {
        const workflowLambda = workflow.factory();
        const workflowImplementation = {
            workflow: workflow,
            workflowLambda: workflowLambda,
            initPromise: this.initWorkflow(workflow.kind, workflowLambda, session)
        };
        return workflowImplementation;
    }
    initWorkflow(workflowKind, workflowLambda, session) {
        var _a;
        const context = new LocalWorkflowContext({
            model: undefined,
            clientMetadata: session ? session.getClientMetadata() : undefined,
            userContext: session ? session.getUserContext() : undefined,
            site: this.site,
            getTokenCallback: (_a = this.getAuthTokenCallback) === null || _a === void 0 ? void 0 : _a.bind(this)
        });
        if (workflowKind === WorkflowKind.SingleItem) {
            return workflowLambda.init(this.site, context);
        }
        else if (workflowKind === WorkflowKind.Join) {
            return workflowLambda.init(this.site, context);
        }
        else {
            return Promise.resolve();
        }
    }
    queueWorkflow(params) {
        this.executionTrackersByWorkflowNameBySession.get(params.session).get(params.workflowInfo.workflow.id).setExecutionState(params.scopeItem.contextId, ExecutionState.Running);
        return this.executeLocalWorkflow(params.workflowInfo, params.scopeItem, params.inputItems, params.session, params.requestedContexts, params.orchestrationMode, params.triggerReason)
            .then(results => {
            // We are processing the annotations produced by the workflow execution here so that it does not
            // block the orchestration code and starts trigerring workflows in sequecence synchronously
            this.processAnnotationResults(results, params.session);
            params.onCompleteCallback();
        })
            .catch((err) => {
            params.onCompleteCallback();
            throw err;
        });
    }
    processAnnotationResults(results, session) {
        for (const annotationResultsMessage of results) {
            session.onAnnotationResults(annotationResultsMessage, AnnotationSource.LocalWorkflow, () => { } /* Empty response callback because we don't need to acknowledge annotations are received */);
        }
    }
    executeLocalWorkflow(workflowInfo, scopeItem, inputItems, session, requestedContexts, orchestrationMode, triggerReason) {
        return new Promise((resolve, reject) => {
            var _a, _b, _c;
            const annotationResultsMessages = [];
            const workflow = workflowInfo.workflow;
            const executeOp = new LogOperation({
                operationName: 'ExecuteWorkflow',
                resourceId: workflow.id,
                joinContextId: (_a = scopeItem === null || scopeItem === void 0 ? void 0 : scopeItem.contextId) !== null && _a !== void 0 ? _a : '',
                resultDescription: triggerReason !== null && triggerReason !== void 0 ? triggerReason : '',
                success: true
            }).setClientMetadata(session.getClientMetadata());
            executeOp.start();
            const contextId = (_b = scopeItem === null || scopeItem === void 0 ? void 0 : scopeItem.contextId) !== null && _b !== void 0 ? _b : inputItems[0].contextId;
            const revisionId = (_c = scopeItem === null || scopeItem === void 0 ? void 0 : scopeItem.revId) !== null && _c !== void 0 ? _c : inputItems[0].revId;
            const buildObjectMap = () => {
                const objectMap = new Map();
                if (scopeItem) {
                    if (!scopeItem.parentPath) {
                        Log.error(0x1f50b257 /* tag_5uljx */, LogCategory.CoreDefault, `Missing scope item parent. Workflow: ${workflow.id}. Type: ${SchemaObject.getTypeNameFor(scopeItem.body)}`);
                    }
                    objectMap.set(scopeItem.body, scopeItem);
                }
                if (Array.isArray(inputItems)) {
                    for (const inputItem of inputItems) {
                        if (inputItem && inputItem.body) {
                            if (!inputItem.parentPath) {
                                Log.error(0x1f50b258 /* tag_5uljy */, LogCategory.CoreDefault, `Missing parent. Workflow: ${workflow.id}. Type: ${SchemaObject.getTypeNameFor(inputItem.body)}`);
                            }
                            objectMap.set(inputItem.body, inputItem);
                        }
                    }
                }
                return objectMap;
            };
            const objectToItemMap = buildObjectMap();
            const endOp = (error, logOp = executeOp) => {
                if (error) {
                    logOp.success = false;
                    logOp.resultDescription += typeof error === 'string' ? error : error.message;
                    logOp.resultSignature = 'Exception';
                    Log.error(0x2224d0e0 /* tag_8jnd6 */, LogCategory.CoreDefault, logOp.stop());
                    return typeof error === 'string' ? new Error(error) : error;
                }
                Log.info(0x2224d0e1 /* tag_8jnd7 */, LogCategory.CoreDefault, logOp.stop());
                return undefined;
            };
            const setAnnotations = (obj, annotationType, annotations, options) => {
                var _a;
                const annotationsOp = new LogOperation({
                    operationName: 'SetAnnotations',
                    resourceId: annotationType,
                    joinContextId: (_a = scopeItem === null || scopeItem === void 0 ? void 0 : scopeItem.contextId) !== null && _a !== void 0 ? _a : '',
                    success: true
                }).setClientMetadata(session.getClientMetadata());
                annotationsOp.start();
                const logAnnotationMetadata = (state) => {
                    Log.info(0x2121d800 /* tag_7i36a */, LogCategory.CoreDefault, new AnnotationMetaDataChange({ annotationType: annotationType, annotationState: state, workflowId: workflowInfo.workflow.id }));
                };
                for (const annotation of annotations) {
                    annotation.metadata = Object.assign(Object.assign({}, annotation.metadata), { state: AnnotationState.Created });
                    logAnnotationMetadata(AnnotationState.Created);
                }
                const item = objectToItemMap.get(obj);
                const itemBodyType = SchemaObject.getTypeNameFor(item.body);
                const createConsolidatedAnnotationResultsMessage = () => {
                    var _a;
                    // Has to be true since only single-item workflows are currently supported
                    if (workflow.kind === WorkflowKind.SingleItem && inputItems[0].body !== obj) {
                        const err = `Expected obj to be ${inputItems[0].body} but instead it was ${obj}`;
                        endOp(err, annotationsOp);
                        reject(endOp(err));
                        return undefined;
                    }
                    let error;
                    // Check that annotations match expectations - otherwise workflow author must fix
                    if (!Array.isArray(annotations)) {
                        error = `Workflow produced an invalid annotation array`;
                    }
                    else if (!workflow.outputTypes || workflow.outputTypes.indexOf(annotationType) < 0) {
                        error = `Workflow said it would output one of [${workflow.outputTypes}] but instead output ${annotationType}`;
                    }
                    else if (!item) {
                        error = `No item provided`;
                    }
                    else {
                        for (const annotation of annotations) {
                            if (!SchemaObject.matchesTypesFor(annotation, [Annotation.getTypeName()])) {
                                error = `Workflow produced an output that is not an annotation ${SchemaObject.getTypeNameFor(annotation)}`;
                            }
                            else if (SchemaObject.getTypeNameFor(annotation) !== annotationType) {
                                error = `Workflow produced inconsistent annotation types in setAnnotations call (${SchemaObject.getTypeNameFor(annotation)} did not match expected ${annotationType})`;
                            }
                        }
                    }
                    if (error) {
                        endOp(error, annotationsOp);
                        reject(endOp(error));
                        return undefined;
                    }
                    // Path to where the annotation will be added
                    let path;
                    if (options && options.isSessionAnnotation) {
                        path = ['session'];
                    }
                    else if (options && options.ancestorType) {
                        path = item.parentPath;
                    }
                    else {
                        path = item.parentPath.concat(item.id);
                    }
                    if (orchestrationMode === LocalWorkflowOrchestrationMode.External) {
                        // Nothing else to do if we're not handling orchestration internally
                        return undefined;
                    }
                    // Changing annotations into items and setting metadata to sent
                    const annotationsToAdd = [];
                    const annotationsToUpdate = [];
                    for (const annotation of annotations) {
                        annotation.metadata = Object.assign(Object.assign({}, annotation.metadata), { state: AnnotationState.Sent });
                        logAnnotationMetadata(AnnotationState.Sent);
                        const annotationId = annotation.id;
                        const existingContextAnnotations = annotationId ? (_a = session.getContextAnnotations(annotationType, AnnotationSource.LocalWorkflow, path, workflow.id)) === null || _a === void 0 ? void 0 : _a.filter(context => { var _a; return ((_a = context.body) === null || _a === void 0 ? void 0 : _a.id) == annotationId; }) : undefined;
                        if ((existingContextAnnotations === null || existingContextAnnotations === void 0 ? void 0 : existingContextAnnotations.length) == 1) {
                            if (existingContextAnnotations.length == 1) {
                                annotationsToUpdate.push({
                                    id: existingContextAnnotations[0].id,
                                    source: workflow.id,
                                    revId: revisionId,
                                    body: annotation,
                                    contextId
                                });
                            }
                            else {
                                Log.error(0x2088d0cc /* tag_68ndm */, LogCategory.CoreDefault, `Assert: Multiple existing context annotations with body id ${annotationId} found for ${annotationType} and local workflow ${workflow.id}).`);
                            }
                        }
                        else {
                            annotationsToAdd.push({
                                id: this.getNextClientAnnotationId(),
                                source: workflow.id,
                                revId: revisionId,
                                body: annotation,
                                contextId
                            });
                        }
                    }
                    const ops = [];
                    if (annotationsToAdd.length > 0) {
                        ops.push(new AddOperation({ parentPath: path, items: annotationsToAdd, parentRevId: revisionId }));
                    }
                    if (annotationsToUpdate.length > 0) {
                        ops.push(new UpdateOperation({ parentPath: path, items: annotationsToUpdate, parentRevId: revisionId }));
                    }
                    return new AnnotationResultsMessage({ annotationType, ops });
                };
                const annotationResultsMessage = createConsolidatedAnnotationResultsMessage();
                if (!annotationResultsMessage) {
                    Log.info(0x1e609017 /* tag_4yjax */, LogCategory.CoreDefault, annotationsOp.stop());
                    return;
                }
                if (options && options.immediate) {
                    session.onAnnotationResults(annotationResultsMessage, AnnotationSource.LocalWorkflow, () => { } /* Empty response callback because we don't need to acknowledge annotations are received */);
                }
                else {
                    annotationResultsMessages.push(annotationResultsMessage);
                }
                // If this is a context annotation, submit it directly to the server, so the server workflows can consume it as well.
                //
                // Note: We're intentionally not checking whether the produced context type is registered locally because we locally
                // don't have a knowledge about requested context types for server workflows and we want to send locally produced
                // context annotation so server workflows can use them.
                if (itemBodyType == UserContextHolder.getTypeName() || itemBodyType == TenantContextHolder.getTypeName() || SchemaObject.matchesTypesFor(item.body, [Document.getTypeName(), SubDocument.getTypeName()])) {
                    session.submitOperationsToSession(annotationResultsMessage.ops);
                }
                Log.info(0x1e609016 /* tag_4yjaw */, LogCategory.CoreDefault, annotationsOp.stop());
            };
            const submitSignals = (signals) => {
                if (orchestrationMode === LocalWorkflowOrchestrationMode.External) {
                    reject(endOp('NYI'));
                    return;
                }
                const logOp = new LogOperation({
                    operationName: 'submitSignalsAction',
                    resourceId: workflow.id,
                    success: true
                }).setClientMetadata(session.getClientMetadata());
                for (const signal of signals) {
                    const signalType = SchemaObject.getTypeNameFor(signal);
                    if (!SchemaObject.matchesTypesFor(signal, [Signal.getTypeName()])) {
                        logOp.resultDescription = `Workflow produced an output that is not an signal (${SchemaObject.getTypeNameFor(signal)})`;
                        Log.info(0x1f142542 /* tag_5fcvc */, LogCategory.CoreDefault, logOp);
                        // throw new Error(WorkflowError[WorkflowError.OutputIsNotSignal]); TODO 6433253: once all workflows have been updated, we turn on this error
                    }
                    if (!workflow.outputTypes || workflow.outputTypes.indexOf(signalType) === -1) {
                        logOp.resultDescription = `Workflow said it would output one of [${workflow.outputTypes}] but instead output ${signalType}`;
                        Log.info(0x1f142541 /* tag_5fcvb */, LogCategory.CoreDefault, logOp);
                        // throw new Error(WorkflowError[WorkflowError.UnexpectedOutput]); TODO 6433253: same as above
                    }
                    if (signal.timestamp) {
                        // We only need to log this once per session per workflow
                        if (!LocalWorkflowExecutionManager.logTimestampUsageByWorkflowId.has(workflow.id)) {
                            LocalWorkflowExecutionManager.logTimestampUsageByWorkflowId.add(workflow.id);
                            logOp.resultDescription = `Workflow "${workflow.id}" sets signal.timeStamp`;
                            Log.info(0x1e59f883 /* tag_4w58d */, LogCategory.CoreDefault, logOp);
                        }
                    }
                }
                // Changing signals into items
                const signalItemsArray = signals.map(signal => {
                    return {
                        id: this.getNextClientSignalId(),
                        source: workflow.id,
                        revId: revisionId,
                        body: signal,
                        contextId
                    };
                });
                const submitOp = new SignalOperation({
                    parentPath: ['session'],
                    parentRevId: revisionId,
                    items: signalItemsArray
                });
                session.submitOperations([submitOp]);
            };
            const overrideWorkflowDefinition = (workflowId, definition, scope) => {
                let contextId;
                switch (scope) {
                    case WorkflowDefinitionScope.JoinContext: {
                        if (!scopeItem.contextId) {
                            const message = 'ContextId is not defined for this scope item.';
                            Log.error(0x1f7096a1 /* tag_52j07 */, LogCategory.CoreDefault, message);
                            throw new Error(message);
                        }
                        contextId = scopeItem.contextId;
                        break;
                    }
                    case WorkflowDefinitionScope.Session: {
                        contextId = undefined;
                        break;
                    }
                    default: {
                        const message = 'Defined scope is not supported. ' + scope;
                        Log.error(0x1f7096a2 /* tag_52j08 */, LogCategory.CoreDefault, message);
                        throw new Error(message);
                    }
                }
                const workflowConfigUpdateMessage = new WorkflowDefinitionOverrideMessage({
                    definition: definition,
                    contextId: contextId,
                    sourceWorkflowId: workflow.id,
                    targetWorkflowId: workflowId
                });
                session.onWorkflowDefinitionOverrideMessage(workflowConfigUpdateMessage);
            };
            const context = new LocalWorkflowContext({
                model: new WorkflowModel(inputItems[0], requestedContexts),
                clientMetadata: session.getClientMetadata(),
                userContext: session.getUserContext(),
                site: this.site,
                getTokenCallback: this.getAuthTokenCallback.bind(this)
            });
            const workflowExecutionDone = (err) => {
                endOp(err ? err.message : undefined);
                if (err) {
                    reject(err);
                }
                else {
                    resolve(annotationResultsMessages);
                }
            };
            const actions = {
                setAnnotations,
                submitSignals,
                done: workflowExecutionDone,
                overrideWorkflowDefinition: overrideWorkflowDefinition,
                getDynamicAnnotations: undefined,
                setBillingDomain: undefined
            };
            const workflowInstance = workflowInfo.workflowLambda;
            if (workflow.kind === WorkflowKind.SingleItem) {
                if (inputItems.length !== 1) {
                    workflowExecutionDone(new Error('Single item workflows expect a single input'));
                }
                context.delta = inputItems[0].delta;
                context.deltas = inputItems[0].deltas;
                try {
                    const singleItemWorkflow = workflowInstance;
                    if (!workflowInfo.initPromise) {
                        workflowInfo.initPromise = singleItemWorkflow.init(this.site, context);
                    }
                    workflowInfo.initPromise.then(() => {
                        singleItemWorkflow.execute(inputItems[0].body, context, actions);
                    }).catch((error) => {
                        endOp(error);
                    });
                }
                catch (error) {
                    endOp(error);
                }
            }
            else if (workflow.kind === WorkflowKind.Join) {
                if (inputItems.length === 0) {
                    workflowExecutionDone(new Error('Join workflows expect an inputs array'));
                }
                context.delta = scopeItem.delta;
                context.deltas = scopeItem.deltas;
                try {
                    const joinWorkflow = workflowInstance;
                    if (!workflowInfo.initPromise) {
                        workflowInfo.initPromise = joinWorkflow.init(this.site, context);
                    }
                    workflowInfo.initPromise.then(() => {
                        joinWorkflow.execute(scopeItem.body, inputItems.map(item => item.body), context, actions);
                    }).catch((error) => {
                        endOp(error);
                    });
                }
                catch (error) {
                    endOp(error);
                }
            }
            else {
                endOp(`Workflow kind ${workflow.kind} not supported`);
            }
        });
    }
    ensureSweepTimer(session) {
        if (!this.sweepTimers.get(session)) {
            const timerId = setInterval(this.onSweep.bind(this), this.sweepIntervalMs);
            this.sweepTimers.set(session, timerId);
        }
    }
    cancelSweepTimer(session) {
        const timerId = this.sweepTimers.get(session);
        if (this.sweepTimers.get(session)) {
            clearInterval(timerId);
            this.sweepTimers.delete(session);
        }
    }
    onSweep() {
        this.sweepScopeExecutionNotifications();
    }
    setScopeExecutionNotification(session, workflowImplementation, scopeItem, orchestrationMode) {
        var _a, _b;
        const startTime = Date.now();
        const minTime = startTime + getSafeNumber(workflowImplementation.workflow.minDelayMs, 1000 /*ms*/);
        const maxTime = startTime + getSafeNumber(workflowImplementation.workflow.maxDelayMs, 5000 /*ms*/);
        const newScopeExecutionNotification = {
            session,
            workflowImplementation,
            scopeItem,
            startTime,
            minTime,
            maxTime,
            orchestrationMode
        };
        const rootItemPathKey = workflowImplementation.workflow.kind === WorkflowKind.Join ? scopeItem.contextId : newScopeExecutionNotification.scopeItem.parentPath.concat(scopeItem.id).join('\\');
        if (!this.pendingScopeExecutionNotificationsByWorkflow.get(workflowImplementation.workflow.id)) {
            this.pendingScopeExecutionNotificationsByWorkflow.set(workflowImplementation.workflow.id, new Map());
        }
        const scopeExecutionNotification = this.pendingScopeExecutionNotificationsByWorkflow.get(workflowImplementation.workflow.id).get(rootItemPathKey);
        if (!scopeExecutionNotification) {
            const logOp = new LogOperation({
                resultDescription: `New pending scope execution for ${workflowImplementation.workflow.id} at ${rootItemPathKey}`,
                operationName: 'LocalScopeExecutionNotification',
                resourceId: workflowImplementation.workflow.id,
                joinContextId: (_a = scopeItem === null || scopeItem === void 0 ? void 0 : scopeItem.contextId) !== null && _a !== void 0 ? _a : '',
                success: true
            }).setClientMetadata(session.getClientMetadata()).start();
            Log.info(0x202df643 /* tag_6l5zd */, LogCategory.CoreDefault, logOp.stop());
            this.pendingScopeExecutionNotificationsByWorkflow.get(workflowImplementation.workflow.id).set(rootItemPathKey, newScopeExecutionNotification);
        }
        else {
            const logOp = new LogOperation({
                resultDescription: `Duplicated pending scope execution for ${workflowImplementation.workflow.id} at ${rootItemPathKey}`,
                operationName: 'LocalScopeExecutionNotification',
                resourceId: workflowImplementation.workflow.id,
                joinContextId: (_b = scopeItem === null || scopeItem === void 0 ? void 0 : scopeItem.contextId) !== null && _b !== void 0 ? _b : '',
                success: true
            }).setClientMetadata(session.getClientMetadata()).start();
            Log.info(0x1e61d49b /* tag_4y3s1 */, LogCategory.CoreDefault, logOp.stop());
        }
        this.ensureSweepTimer(session);
    }
    sweepScopeExecutionNotifications() {
        var _a, _b;
        const cancelTimers = new Set(Array.from(this.sweepTimers.keys()));
        for (const [workflowId, scopeExecutionNotificationsMap] of Array.from(this.pendingScopeExecutionNotificationsByWorkflow.entries())) {
            for (const [rootItemPathKey, scopeExecutionNotification] of Array.from(scopeExecutionNotificationsMap.entries())) {
                const { session, workflowImplementation: { workflow }, orchestrationMode } = scopeExecutionNotification;
                const workflowItemStorage = session.getWorkflowItemStorage();
                const logOp = new LogOperation({
                    operationName: 'LocalScopeExecutionNotification',
                    resourceId: workflow.id,
                    joinContextId: (_b = (_a = scopeExecutionNotification.scopeItem) === null || _a === void 0 ? void 0 : _a.contextId) !== null && _b !== void 0 ? _b : '',
                    success: true
                }).setClientMetadata(session.getClientMetadata()).start();
                const validExecution = () => {
                    if (workflow.kind === WorkflowKind.Join) {
                        const now = Date.now();
                        const { workflow } = scopeExecutionNotification.workflowImplementation;
                        const contextId = scopeExecutionNotification.scopeItem.contextId;
                        const maxDelayMs = session.getWorkflowDefinition(workflow, contextId).maxDelayMs;
                        if (workflowItemStorage.isWorkflowReady(contextId, workflow)) {
                            // Check annotations count condition
                            logOp.resultDescription = `Join Workflow: ${workflow.id} queuing by maxAnnotation, contextId: ${contextId}`;
                            Log.info(0x1f796352 /* tag_54wns */, LogCategory.CoreDefault, logOp.stop());
                            return { isValid: true, triggerReason: TriggerReason.JoinMaxAnnnotation };
                        }
                        else if (scopeExecutionNotification.startTime + maxDelayMs < now) {
                            // Check maxDelay condition
                            logOp.resultDescription = `Join Workflow: ${workflow.id} queuing by maxTimeout, contextId: ${contextId}`;
                            Log.info(0x1f796353 /* tag_54wnt */, LogCategory.CoreDefault, logOp.stop());
                            return { isValid: true, triggerReason: TriggerReason.JoinMaxTimeout };
                        }
                        else if (this.isReadyToEarlyJoin(this.executionTrackersByWorkflowNameBySession.get(session).get(workflow.id), contextId, session.getClientMetadata())) {
                            // Check early join condition
                            Log.info(0x1e8ce7c8 /* tag_49o5i */, LogCategory.CoreDefault, `Workflow: ${workflow.id} queuing by early completion, contextId: ${contextId}, timeout: ${maxDelayMs}`);
                            return { isValid: true, triggerReason: TriggerReason.JoinEarlyCompletion };
                        }
                        else {
                            return { isValid: false, triggerReason: TriggerReason.Unknown };
                        }
                    }
                    return { isValid: true, triggerReason: TriggerReason.Unknown };
                };
                const tryToExecuteNotification = () => {
                    const { isValid, triggerReason } = validExecution();
                    if (!isValid) {
                        return false;
                    }
                    const [areRequiredContextsReady, requestedContexts] = session.resolveRequestedContexts(workflow);
                    if (!areRequiredContextsReady) {
                        return false;
                    }
                    try {
                        if (workflow.kind === WorkflowKind.Join) {
                            const contextId = scopeExecutionNotification.scopeItem.contextId;
                            const collectionScopeItem = workflowItemStorage.getScopeItem(contextId, workflow);
                            if (collectionScopeItem) {
                                const inputItems = workflowItemStorage.getItemsToExecute(collectionScopeItem.contextId, workflow);
                                if (inputItems.length === 0) {
                                    logOp.resultDescription = `Failed to retrieve items for workflow: ${workflow.id}, contextId: ${contextId}, skipping execution`;
                                    Log.info(0x1f7096a3 /* tag_52j09 */, LogCategory.CoreDefault, logOp.stop());
                                    this.onWorkflowExecuted(collectionScopeItem, workflow, session);
                                    return true;
                                }
                                this.queueWorkflow({
                                    workflowInfo: scopeExecutionNotification.workflowImplementation,
                                    scopeItem: collectionScopeItem,
                                    inputItems,
                                    requestedContexts,
                                    session,
                                    orchestrationMode,
                                    triggerReason,
                                    onCompleteCallback: this.onWorkflowExecuted.bind(this, collectionScopeItem, workflow, session)
                                }).catch((err) => {
                                    logOp.success = false;
                                    logOp.resultDescription = err.message;
                                    Log.error(0x1e58215d /* tag_4wcf3 */, LogCategory.CoreDefault, logOp.stop());
                                });
                            }
                            else {
                                logOp.resultDescription = `ContextId no longer exists, skipping workflow execution`;
                                Log.info(0x202df644 /* tag_6l5ze */, LogCategory.CoreDefault, logOp.stop());
                            }
                        }
                        else {
                            logOp.resultDescription = `Workflow in type ${workflow.kind} is not supported`;
                            Log.error(0x202df645 /* tag_6l5zf */, LogCategory.CoreDefault, logOp.stop());
                        }
                    }
                    catch (error) {
                        logOp.resultDescription = `Trying to execute ${workflow.id} caused an exception: ${error}`;
                        Log.warn(0x202df646 /* tag_6l5zg */, LogCategory.CoreDefault, logOp.stop());
                    }
                    return true;
                };
                if (tryToExecuteNotification()) {
                    this.pendingScopeExecutionNotificationsByWorkflow.get(workflowId).delete(rootItemPathKey);
                    if (this.pendingScopeExecutionNotificationsByWorkflow.get(workflowId).size === 0) {
                        this.pendingScopeExecutionNotificationsByWorkflow.delete(workflowId);
                    }
                }
                else {
                    // At least one notification is not yet ready to fire, keep the timer.
                    cancelTimers.delete(session);
                }
            }
        }
        if (cancelTimers.size !== 0) {
            for (const session of Array.from(cancelTimers)) {
                const logOp = new LogOperation({ resultDescription: 'No pending scope notifications left, cancelling sweep timer', operationName: 'LocalScopeExecutionNotification', success: true }).start();
                Log.debug(0x202df647 /* tag_6l5zh */, LogCategory.CoreDefault, logOp.stop());
                this.cancelSweepTimer(session);
            }
        }
    }
    onExternalWorkflowExecuted(contextId, workflowId, session) {
        const hollowItem = { id: '', parentPath: [], contextId };
        const hollowWorkflow = { id: workflowId };
        this.onWorkflowExecuted(hollowItem, hollowWorkflow, session, false /** isLocalWorkflow */);
    }
    onWorkflowExecuted(scopeItem, workflow, session, isLocalWorkflow = true) {
        var _a;
        (_a = this.executionTrackersByWorkflowNameBySession.get(session).get(workflow.id)) === null || _a === void 0 ? void 0 : _a.afterWorkflowExecution(scopeItem.contextId);
        if (isLocalWorkflow && workflow.kind === WorkflowKind.Join) {
            session.getWorkflowItemStorage().onWorkflowExecuted(scopeItem, workflow);
        }
    }
}
//# sourceMappingURL=local-workflow-execution-manager.js.map